
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import type { Database } from "@/integrations/supabase/types";

type SupportedLanguage = Database["public"]["Enums"]["supported_language"];

interface Question {
  id: string;
  type: string;
  question: string;
  required: boolean;
  options?: string[];
  min?: number;
  max?: number;
}

interface QuestionRendererProps {
  question: Question;
  value: string;
  onChange: (value: string) => void;
  language: SupportedLanguage;
}

// RTL languages
const RTL_LANGUAGES = ["ar", "fa", "prs"];

export const QuestionRenderer = ({
  question,
  value,
  onChange,
  language,
}: QuestionRendererProps) => {
  const isRtl = RTL_LANGUAGES.includes(language);
  
  // Check if question is valid
  if (!question || !question.question) {
    console.error("Invalid question object:", question);
    return (
      <div className="p-2 border border-red-200 bg-red-50 rounded">
        <p className="text-red-500">Question not available</p>
      </div>
    );
  }
  
  // Parse multiple selection values for checkbox
  const selectedValues = value ? value.split(',') : [];
  
  // Handle checkbox change
  const handleCheckboxChange = (option: string, checked: boolean) => {
    let newSelectedValues = [...selectedValues];
    
    if (checked) {
      // Add the option if it's not already in the array
      if (!newSelectedValues.includes(option)) {
        newSelectedValues.push(option);
      }
    } else {
      // Remove the option if it exists
      newSelectedValues = newSelectedValues.filter(val => val !== option);
    }
    
    // Join with commas and update via onChange
    onChange(newSelectedValues.join(','));
  };
  
  // Log the question data for debugging
  console.log(`Rendering question ${question.id} with value: "${value}" and type: ${question.type}`);
  
  return (
    <div className="space-y-2 w-full" dir={isRtl ? "rtl" : "ltr"}>
      <Label 
        htmlFor={question.id} 
        className="block w-full"
      >
        <span className="flex items-start gap-1">
          {question.question}
          {question.required && <span className="text-destructive">*</span>}
        </span>
      </Label>
      
      {question.type === "text" ? (
        <Input
          id={question.id}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          required={question.required}
          aria-required={question.required}
        />
      ) : question.type === "number" ? (
        <Input
          id={question.id}
          type="number"
          min={question.min}
          max={question.max}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          required={question.required}
          aria-required={question.required}
        />
      ) : question.type === "radio" && question.options ? (
        <RadioGroup
          value={value || ""}
          onValueChange={onChange}
          required={question.required}
          className="w-full"
        >
          {question.options.map((option, index) => (
            <div 
              key={`${question.id}-${index}`} 
              className={`flex items-center gap-2 ${isRtl ? 'flex-row-reverse justify-start' : ''}`}
            >
              <RadioGroupItem 
                value={option} 
                id={`${question.id}-${index}`} 
                data-index={index} 
              />
              <Label htmlFor={`${question.id}-${index}`}>
                {option}
              </Label>
            </div>
          ))}
        </RadioGroup>
      ) : question.type === "checkbox" && question.options ? (
        <div className={`space-y-2 w-full ${isRtl ? 'text-right' : 'text-left'}`}>
          {question.options.map((option, index) => (
            <div 
              key={`${question.id}-${index}`} 
              className={`flex items-center gap-2 ${isRtl ? 'flex-row-reverse justify-end' : 'justify-start'}`}
            >
              {isRtl ? (
                <>
                  <Label 
                    htmlFor={`${question.id}-${index}`}
                    className="cursor-pointer text-right"
                  >
                    {option}
                  </Label>
                  <Checkbox 
                    id={`${question.id}-${index}`}
                    checked={selectedValues.includes(option)}
                    onCheckedChange={(checked) => handleCheckboxChange(option, !!checked)}
                  />
                </>
              ) : (
                <>
                  <Checkbox 
                    id={`${question.id}-${index}`}
                    checked={selectedValues.includes(option)}
                    onCheckedChange={(checked) => handleCheckboxChange(option, !!checked)}
                  />
                  <Label 
                    htmlFor={`${question.id}-${index}`}
                    className="cursor-pointer text-left"
                  >
                    {option}
                  </Label>
                </>
              )}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};
